/**
 * Comparator that mirrors EdgeDeployer's PATH_DESTINATION_PAIR_COMPARATOR:
 *   1. priority DESC (higher numbers first)
 *   2. segment count DESC (more segments = deeper = more specific)
 *   3. lexicographic ASC on path (tiebreaker of last resort)
 *
 * Used to sort both the static host config (Pattern A) and the runtime
 * route map (Pattern B) so <Switch> matches routes in the same order the
 * CDN would on a hard navigation.
 */
export function graniteRouteComparator(a, b) {
  const priorityDiff = b.priority - a.priority;
  if (priorityDiff !== 0) return priorityDiff;
  const aSegments = a.path.split('/').filter(Boolean).length;
  const bSegments = b.path.split('/').filter(Boolean).length;
  const segmentDiff = bSegments - aSegments;
  if (segmentDiff !== 0) return segmentDiff;
  return a.path.localeCompare(b.path);
}

/**
 * Resolves the host's remoteRoutes config into sorted route definitions
 * and a map of local route overrides for the link rewriter.
 *
 * Routes are sorted most-specific-first (by segment count) so the
 * Switch in GraniteRoutes matches deeper paths before shallower ones,
 * e.g. /app/:portalId/record matches before /app/:portalId.
 */
export function resolveGraniteRoutes(remoteRoutes, portalId) {
  const seenModules = new Set();
  const resolvedRoutes = [];
  const localRouteOverrides = {};
  remoteRoutes.forEach(route => {
    var _route$priority, _route$localRedirects;
    seenModules.add(route.remoteModule);

    // Resolve the path template into a concrete path for react-router
    resolvedRoutes.push({
      path: route.path.replace(':portalId', `${portalId}`),
      remoteModule: route.remoteModule,
      priority: (_route$priority = route.priority) !== null && _route$priority !== void 0 ? _route$priority : 0
    });

    // Register localRedirects so the link rewriter can intercept clicks
    // to legacy paths and redirect them to the granite path
    (_route$localRedirects = route.localRedirects) === null || _route$localRedirects === void 0 || _route$localRedirects.forEach(pathToRedirect => {
      localRouteOverrides[pathToRedirect] = {
        path: route.path,
        remoteModule: route.remoteModule
      };
    });

    // The route's own path is also an override so same-app navigations
    // are handled locally instead of triggering a full page load
    localRouteOverrides[route.path] = {
      path: route.path,
      remoteModule: route.remoteModule
    };
  });
  resolvedRoutes.sort(graniteRouteComparator);
  return {
    resolvedRoutes,
    remoteModules: Array.from(seenModules),
    localRouteOverrides
  };
}